import React, { useState, useEffect, useCallback, useRef } from 'react';
import { devFilesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import {
  RefreshCw, Plus, Trash2, X, Save, Pencil, Tag, Calendar, FileText,
  ChevronLeft, ChevronRight, ScanFace
, Search, GripVertical , History } from 'lucide-react';

/**
 * RevLogPage - Development page to view/edit rev-log.json (Release Notes)
 * Uses unified list/detail pattern matching data modules
 * TODO: Delete this file before production deployment
 */
const RevLogPage = () => {
  const [revLog, setRevLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRevision, setSelectedRevision] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAddRevision, setShowAddRevision] = useState(false);
  const [newRevision, setNewRevision] = useState({ type: 'minor', summary: '', changes: [''] });
  const [listCollapsed, setListCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const menuRef = useRef(null);
  const { notify } = useNotifications();

  // Get filtered revisions
  const getFilteredRevisions = () => {
    if (!revLog?.revisions) return [];
    return revLog.revisions;
  };

  // Track if edit form has changes
  const hasChanges = isEditing && editForm && selectedRevision && (
    editForm.type !== selectedRevision.type ||
    editForm.summary !== selectedRevision.summary ||
    JSON.stringify(editForm.changes) !== JSON.stringify(selectedRevision.changes)
  );

  const loadRevLog = useCallback(async () => {
    try {
      setLoading(true);
      // error cleared
      const response = await devFilesApi.getRevLog();
      setRevLog(response.data);
      // Auto-select the current/first version
      if (!selectedRevision && response.data.revisions.length > 0) {
        setSelectedRevision(response.data.revisions[0]);
      }
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to load revision log');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRevision]);

  useEffect(() => {
    loadRevLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-select revision when revLog updates
  useEffect(() => {
    if (revLog && selectedRevision) {
      const updated = revLog.revisions.find(r => r.version === selectedRevision.version);
      if (updated) {
        setSelectedRevision(updated);
      } else if (revLog.revisions.length > 0) {
        setSelectedRevision(revLog.revisions[0]);
      } else {
        setSelectedRevision(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revLog]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectRevision = (revision) => {
    if (isEditing) {
      notify.confirm('Discard unsaved changes?', () => {
        setIsEditing(false);
        setEditForm({});
        setSelectedRevision(revision);
      });
      return;
    }
    setSelectedRevision(revision);
  };

  const startEditing = () => {
    if (!selectedRevision) return;
    setEditForm({ ...selectedRevision, changes: [...selectedRevision.changes] });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await devFilesApi.updateRevision(selectedRevision.id, editForm);
      setIsEditing(false);
      setEditForm({});
      await loadRevLog();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to save revision');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRevision = async () => {
    if (!newRevision.summary.trim()) return;

    // Filter out empty changes
    const filteredChanges = newRevision.changes.filter(c => c.trim());
    if (filteredChanges.length === 0) return;

    try {
      setSaving(true);
      await devFilesApi.addRevision({
        type: newRevision.type,
        summary: newRevision.summary,
        changes: filteredChanges,
      });
      setNewRevision({ type: 'minor', summary: '', changes: [''] });
      setShowAddRevision(false);
      await loadRevLog();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to add revision');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRevision = () => {
    if (!selectedRevision) return;
    if (revLog.revisions.length === 1) {
      notify.error('Cannot delete the only revision');
      return;
    }
    notify.confirm(`Delete revision ${selectedRevision.version}?`, async () => {
      try {
        setSaving(true);
        await devFilesApi.deleteRevision(selectedRevision.id);
        setIsEditing(false);
        setSelectedRevision(null);
        await loadRevLog();
      } catch (err) {
        notify.error(err.response?.data?.message || 'Failed to delete revision');
      } finally {
        setSaving(false);
      }
    });
  };

  // Add/remove change lines in forms
  const addChangeLine = (isEdit = false) => {
    if (isEdit) {
      setEditForm({ ...editForm, changes: [...editForm.changes, ''] });
    } else {
      setNewRevision({ ...newRevision, changes: [...newRevision.changes, ''] });
    }
  };

  const updateChangeLine = (index, value, isEdit = false) => {
    if (isEdit) {
      const newChanges = [...editForm.changes];
      newChanges[index] = value;
      setEditForm({ ...editForm, changes: newChanges });
    } else {
      const newChanges = [...newRevision.changes];
      newChanges[index] = value;
      setNewRevision({ ...newRevision, changes: newChanges });
    }
  };

  const removeChangeLine = (index, isEdit = false) => {
    if (isEdit) {
      const newChanges = editForm.changes.filter((_, i) => i !== index);
      setEditForm({ ...editForm, changes: newChanges.length ? newChanges : [''] });
    } else {
      const newChanges = newRevision.changes.filter((_, i) => i !== index);
      setNewRevision({ ...newRevision, changes: newChanges.length ? newChanges : [''] });
    }
  };

  const getTypeClass = (type) => {
    return type === 'major' ? 'type-major' : 'type-minor';
  };

  if (loading) {
    return <div className="dev-page loading">Loading...</div>;
  }

  return (
    <div className="module-container">
      {/* Header - Pattern 1 */}
      <div className="module-header">
        <div className="module-header-left">
          <History size={20} style={{ color: 'var(--color-primary)' }} />
          <h1>Rev Log</h1>
          <span className="module-header-meta">
            v{revLog?.currentVersion} &#8226; {revLog?.revisions.length} revision{revLog?.revisions.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="module-header-actions">
          {isEditing ? (
            <button className="crud-btn" onClick={handleDeleteRevision} disabled={saving || revLog?.revisions.length === 1} title="Delete" style={{ color: 'var(--color-error)' }}><Trash2 size={18} /></button>
          ) : (
            <button className="crud-btn" onClick={() => setShowAddRevision(true)} title="Add Revision"><Plus size={18} /></button>
          )}
          <button className={`crud-btn ${!selectedRevision && !isEditing ? 'disabled' : ''}`} onClick={isEditing ? cancelEditing : startEditing} disabled={!selectedRevision && !isEditing} title={isEditing ? 'Cancel' : 'Edit'}>
            {isEditing ? <X size={18} /> : <Pencil size={18} />}
          </button>
          <button className={`crud-btn ${!isEditing || !hasChanges ? 'disabled' : ''}`} onClick={handleSave} disabled={!isEditing || saving || !hasChanges} title="Save"><Save size={18} /></button>
          <div className="action-menu" ref={menuRef}>
            <button className="action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="action-menu-dropdown" style={{ minWidth: '180px' }}>
                <button className="action-menu-item" onClick={() => { setMenuOpen(false); loadRevLog(); }}><RefreshCw size={16} /><span>Refresh</span></button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="list-view">
        {listCollapsed ? (
          <div className="list-panel-collapsed">
            <button onClick={() => setListCollapsed(false)} className="list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button>
          </div>
        ) : (
          <div className="list-panel">
            <div className="list-panel-header">
              <button onClick={() => setListCollapsed(true)} className="list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
              <div style={{ flex: 1, position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: '8px', top: '10px', color: 'var(--color-gray-400)' }} />
                <input className="form-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search revisions..." style={{ paddingLeft: '28px', width: '100%' }} />
              </div>
            </div>
            <div className="list-scroll">
              {getFilteredRevisions().filter(r => !searchTerm || r.version?.includes(searchTerm) || r.summary?.toLowerCase().includes(searchTerm.toLowerCase())).map((revision) => (
                <button key={revision.version} onClick={() => handleSelectRevision(revision)} className={`list-item-btn ${selectedRevision?.version === revision.version ? 'selected' : ''}`}>
                  <span className="list-item-primary">
                    <span className={`item-badge ${getTypeClass(revision.type)}`} style={{ marginRight: '6px', fontSize: '9px', padding: '1px 4px', borderRadius: '8px' }}>
                      {revision.type === 'major' ? 'MAJ' : 'MIN'}
                    </span>
                    {revision.version}
                  </span>
                  <span className="list-item-secondary">{revision.date}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Detail Panel */}
        <div className="detail-panel">
          {selectedRevision ? (
            <>
              <div className="dev-detail-header">
                <div className="dev-detail-title">
                  <h2>
                    <Tag size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    {selectedRevision.version}
                  </h2>
                  <span className={`rev-type-badge ${getTypeClass(selectedRevision.type)}`}>
                    {selectedRevision.type === 'major' ? 'MAJOR' : 'MINOR'}
                  </span>
                </div>
                {/* CRUD actions moved to page header */}
              </div>

              <div className="dev-detail-body">
                {isEditing ? (
                  <div className="dev-detail-form">
                    <div className="form-row form-row-inline">
                      <div className="form-field">
                        <label>Type</label>
                        <select
                          className="form-input"
                          value={editForm.type}
                          onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                        >
                          <option value="minor">Minor (sprint completion)</option>
                          <option value="major">Major (dev step completion)</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-row">
                      <label>Summary</label>
                      <input
                        type="text"
                        className="form-input"
                        value={editForm.summary}
                        onChange={(e) => setEditForm({ ...editForm, summary: e.target.value })}
                      />
                    </div>
                    <div className="form-row">
                      <label>Changes</label>
                      <div className="changes-list-edit">
                        {editForm.changes.map((change, index) => (
                          <div key={index} className="change-line-row">
                            <input
                              type="text"
                              className="form-input"
                              value={change}
                              onChange={(e) => updateChangeLine(index, e.target.value, true)}
                              placeholder="Describe a change..."
                            />
                            <button
                              className="btn-icon-subtle"
                              onClick={() => removeChangeLine(index, true)}
                              disabled={editForm.changes.length === 1}
                              title="Remove"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => addChangeLine(true)}
                          style={{ marginTop: '0.5rem' }}
                        >
                          <Plus size={14} /> Add Change
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="dev-detail-view">
                    <div className="detail-section">
                      <label>
                        <Calendar size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                        Date
                      </label>
                      <p>{selectedRevision.date}</p>
                    </div>
                    <div className="detail-section">
                      <label>
                        <FileText size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                        Summary
                      </label>
                      <p>{selectedRevision.summary}</p>
                    </div>
                    <div className="detail-section">
                      <label>Changes</label>
                      <ul className="changes-list">
                        {selectedRevision.changes.map((change, index) => (
                          <li key={index}>{change}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="dev-detail-empty">
              <p>Select a revision to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Revision Modal */}
      {showAddRevision && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Add Revision</h3>
              <button onClick={() => setShowAddRevision(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row form-row-inline">
                <div className="form-field">
                  <label>Type</label>
                  <select
                    className="form-input"
                    value={newRevision.type}
                    onChange={(e) => setNewRevision({ ...newRevision, type: e.target.value })}
                  >
                    <option value="minor">Minor (sprint completion)</option>
                    <option value="major">Major (dev step completion)</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <label>Summary</label>
                <input
                  type="text"
                  className="form-input"
                  value={newRevision.summary}
                  onChange={(e) => setNewRevision({ ...newRevision, summary: e.target.value })}
                  placeholder="Brief description of this release"
                  autoFocus
                />
              </div>
              <div className="form-row">
                <label>Changes</label>
                {newRevision.changes.map((change, index) => (
                  <div key={index} className="change-line-row">
                    <input
                      type="text"
                      className="form-input"
                      value={change}
                      onChange={(e) => updateChangeLine(index, e.target.value)}
                      placeholder="Describe a change..."
                    />
                    <button
                      className="btn-icon-subtle"
                      onClick={() => removeChangeLine(index)}
                      disabled={newRevision.changes.length === 1}
                      title="Remove"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => addChangeLine()}
                  style={{ marginTop: '0.5rem' }}
                >
                  <Plus size={14} /> Add Change
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddRevision(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddRevision}
                disabled={saving || !newRevision.summary.trim() || !newRevision.changes.some(c => c.trim())}
              >
                {saving ? 'Adding...' : 'Add Revision'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevLogPage;
